import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AssessmentColumn, MarkSheet, RankedStudentRow, StudentScoreRow } from '../models/grades.model';
import { GradesService } from '../services/grades.service';
import { StudentService } from '../services/student.service';
import { Student } from '../student';
import { CLASS_GROUP_OPTIONS } from '../school-constants';
import { LocalStorageService } from '../services/local-storage.service';

@Component({
  selector: 'app-grades',
  templateUrl: './grades.component.html',
  styleUrls: ['./grades.component.css']
})
export class GradesComponent implements OnInit {

  readonly classOptions = [...CLASS_GROUP_OPTIONS];

  filterForm: FormGroup;
  examTitleInput = '';
  /** '' = none selected; 'NEW' = composing new sheet; else MarkSheet.id */
  selectedSheetKey: string | null = null;

  allSheets: MarkSheet[] = [];
  /** Working sheet (same reference as entry in allSheets when editing existing). */
  activeSheet: MarkSheet | null = null;
  isNewSheet = false;

  loading = false;
  saving = false;

  tabIndex = 0;

  constructor(
    private fb: FormBuilder,
    private gradesService: GradesService,
    private studentService: StudentService,
    private snackBar: MatSnackBar,
    private localStorage: LocalStorageService
  ) { }

  ngOnInit(): void {
    const y = new Date().getFullYear();
    this.filterForm = this.fb.group({
      academicYear: [`${y}-${y + 1}`, Validators.required],
      className: ['', Validators.required]
    });
    this.refreshSheets();
  }

  get isAdmin(): boolean {
    return this.localStorage.isAdmin();
  }

  get filteredSheets(): MarkSheet[] {
    const y = (this.filterForm?.value?.academicYear as string)?.trim();
    const c = (this.filterForm?.value?.className as string)?.trim();
    if (!y || !c) {
      return [];
    }
    return this.allSheets.filter(s => s.academicYear === y && s.className === c);
  }

  get rankedRows(): RankedStudentRow[] {
    if (!this.activeSheet || !this.activeSheet.rows.length) {
      return [];
    }
    return this.gradesService.rankStudents(this.activeSheet);
  }

  refreshSheets(): void {
    this.loading = true;
    this.gradesService.getMarkSheets().subscribe({
      next: sheets => {
        this.allSheets = sheets || [];
        this.loading = false;
        this.reconcileSelection();
      },
      error: () => {
        this.allSheets = [];
        this.loading = false;
      }
    });
  }

  private reconcileSelection(): void {
    if (!this.activeSheet || this.isNewSheet) {
      return;
    }
    const found = this.allSheets.find(s => s.id === this.activeSheet?.id);
    if (!found) {
      this.activeSheet = null;
      this.selectedSheetKey = null;
      this.isNewSheet = false;
    }
  }

  onSelectSheet(key: string | null): void {
    this.selectedSheetKey = key || null;
    if (!key) {
      this.isNewSheet = false;
      this.activeSheet = null;
      this.examTitleInput = '';
      return;
    }
    if (key === 'NEW') {
      this.startNewSheet();
      return;
    }
    const sheet = this.allSheets.find(s => s.id === key);
    this.isNewSheet = false;
    this.activeSheet = sheet || null;
    this.examTitleInput = sheet?.examTitle || '';
  }

  startNewSheet(): void {
    if (this.filterForm.invalid) {
      this.snackBar.open('Choose academic year and class first.', 'Dismiss', { duration: 4000 });
      this.selectedSheetKey = null;
      return;
    }
    const academicYear = (this.filterForm.value.academicYear as string).trim();
    const className = (this.filterForm.value.className as string).trim();
    const title = (this.examTitleInput || '').trim() || 'Examination';
    this.activeSheet = {
      id: this.gradesService.newSheetId(),
      academicYear,
      className,
      examTitle: title,
      columns: [
        {
          id: this.gradesService.newColumnId(),
          subject: 'Subject 1',
          maxMarks: 100
        }
      ],
      rows: []
    };
    this.isNewSheet = true;
    this.selectedSheetKey = 'NEW';
  }

  addColumn(): void {
    if (!this.activeSheet) {
      return;
    }
    const col: AssessmentColumn = {
      id: this.gradesService.newColumnId(),
      subject: `Subject ${this.activeSheet.columns.length + 1}`,
      maxMarks: 50
    };
    this.activeSheet.columns.push(col);
    for (const row of this.activeSheet.rows) {
      row.scores[col.id] = row.scores[col.id] ?? 0;
    }
  }

  removeColumn(col: AssessmentColumn): void {
    if (!this.activeSheet || this.activeSheet.columns.length <= 1) {
      return;
    }
    this.activeSheet.columns = this.activeSheet.columns.filter(c => c.id !== col.id);
    for (const row of this.activeSheet.rows) {
      delete row.scores[col.id];
    }
  }

  loadStudents(): void {
    if (!this.activeSheet || this.filterForm.invalid) {
      this.snackBar.open('Choose class and year, and open or create a mark sheet.', 'Dismiss', { duration: 4000 });
      return;
    }
    const className = (this.filterForm.value.className as string).trim();
    this.loading = true;
    this.studentService.getAll().subscribe({
      next: students => {
        const inClass = (students || []).filter(
          (s: Student) => (s.className || '').trim() === className && s.id != null
        );
        this.mergeStudentRows(inClass);
        this.loading = false;
        if (!inClass.length) {
          this.snackBar.open('No students in this class.', 'Dismiss', { duration: 5000 });
        } else {
          this.snackBar.open(`Loaded ${inClass.length} student(s).`, 'Dismiss', { duration: 3000 });
        }
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Could not load students.', 'Dismiss', { duration: 5000 });
      }
    });
  }

  private mergeStudentRows(students: Student[]): void {
    if (!this.activeSheet) {
      return;
    }
    const prevById = new Map(this.activeSheet.rows.map(r => [r.studentId, r]));
    const next: StudentScoreRow[] = [];
    for (const s of students) {
      if (s.id == null) {
        continue;
      }
      const prev = prevById.get(s.id);
      const scores: Record<string, number> = { ...(prev?.scores || {}) };
      for (const c of this.activeSheet.columns) {
        if (scores[c.id] === undefined || scores[c.id] === null) {
          scores[c.id] = 0;
        }
      }
      next.push({
        studentId: s.id,
        firstName: s.firstName || '',
        lastName: s.lastName || '',
        rollNumber: s.rollNumber,
        scores
      });
    }
    next.sort((a, b) => {
      const ra = (a.rollNumber || '').toString();
      const rb = (b.rollNumber || '').toString();
      if (ra && rb && ra !== rb) {
        return ra.localeCompare(rb, undefined, { numeric: true });
      }
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });
    this.activeSheet.rows = next;
  }

  patchScore(row: StudentScoreRow, colId: string, max: number, raw: string | number): void {
    let n = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (Number.isNaN(n)) {
      n = 0;
    }
    n = Math.max(0, Math.min(max, n));
    row.scores[colId] = n;
  }

  rowPercent(row: StudentScoreRow): number {
    if (!this.activeSheet) {
      return 0;
    }
    return Math.round(this.gradesService.rowTotals(this.activeSheet, row).percent * 100) / 100;
  }

  rowLetter(row: StudentScoreRow): string {
    if (!this.activeSheet) {
      return '—';
    }
    const p = this.gradesService.rowTotals(this.activeSheet, row).percent;
    return this.gradesService.letterGrade(p);
  }

  save(): void {
    if (!this.activeSheet) {
      return;
    }
    if (!this.activeSheet.columns.length) {
      this.snackBar.open('Add at least one subject column.', 'Dismiss', { duration: 4000 });
      return;
    }
    this.activeSheet.academicYear = (this.filterForm.value.academicYear as string).trim();
    this.activeSheet.className = (this.filterForm.value.className as string).trim();
    const title = (this.examTitleInput || '').trim();
    if (title) {
      this.activeSheet.examTitle = title;
    }

    let list = [...this.allSheets];
    const idx = list.findIndex(s => s.id === this.activeSheet?.id);
    if (idx >= 0) {
      list[idx] = this.activeSheet;
    } else {
      list.push(this.activeSheet);
    }

    this.saving = true;
    this.gradesService.saveMarkSheets(list).subscribe({
      next: () => {
        this.allSheets = list;
        this.isNewSheet = false;
        this.selectedSheetKey = this.activeSheet?.id || null;
        this.saving = false;
        this.snackBar.open('Mark sheet saved.', 'Dismiss', { duration: 3000 });
      },
      error: () => {
        this.saving = false;
        this.snackBar.open('Save failed.', 'Dismiss', { duration: 5000 });
      }
    });
  }

  deleteActiveSheet(): void {
    if (!this.isAdmin || !this.activeSheet || this.isNewSheet) {
      return;
    }
    const list = this.allSheets.filter(s => s.id !== this.activeSheet?.id);
    this.saving = true;
    this.gradesService.saveMarkSheets(list).subscribe({
      next: () => {
        this.allSheets = list;
        this.activeSheet = null;
        this.selectedSheetKey = null;
        this.saving = false;
        this.snackBar.open('Mark sheet removed.', 'Dismiss', { duration: 3000 });
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  /** When class/year changes, clear incompatible selection. */
  onFilterChange(): void {
    const y = (this.filterForm.value.academicYear as string)?.trim();
    const c = (this.filterForm.value.className as string)?.trim();
    if (
      this.activeSheet &&
      !this.isNewSheet &&
      (this.activeSheet.academicYear !== y || this.activeSheet.className !== c)
    ) {
      this.activeSheet = null;
      this.selectedSheetKey = null;
    }
  }

  trackByStudentId(_i: number, row: StudentScoreRow): number {
    return row.studentId;
  }

  trackByColId(_i: number, col: AssessmentColumn): string {
    return col.id;
  }
}
